import {
  Check,
  Clipboard,
  DownloadSimple,
  FileDoc,
  FilePdf,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { useToast } from "../components/ui/Toast";
import { demoApplication } from "../data/mock";
import { useAssessmentSession } from "../features/session/AssessmentSessionContext";
import { getAssessmentSnapshot, getDownloadUrl } from "../services/api";
const answers: [string, string][] = [
  ["Campaign Scope", "Low Volume Mixed · DBA"],
  ["Legal Business Name", demoApplication.legalBusinessName],
  ["DBA / Brand Name", demoApplication.brandName],
  ["Website", demoApplication.websiteUrl],
  ["Industry", demoApplication.industry],
  ["Campaign Use Case", demoApplication.campaignUseCase],
  ["Campaign Description", demoApplication.campaignDescription],
  ["Message Flow", demoApplication.optInSteps],
  ["Marketing Consent", demoApplication.marketingConsent],
  ["Transactional Consent", demoApplication.transactionalConsent],
  ["Sample Message 1", demoApplication.messages.sample1],
  [
    "Sample Message 2",
    "Hi [First Name], your technician from [Brand Name] is on the way. Reply STOP to opt out or HELP for assistance.",
  ],
  ["Sample Message 3", demoApplication.messages.sample3],
  ["Opt-In Confirmation", demoApplication.messages.confirmation],
  ["STOP Response", demoApplication.messages.stop],
  ["HELP Response", demoApplication.messages.help],
  ["Privacy Policy URL", demoApplication.privacyPolicyUrl],
  ["Terms URL", demoApplication.termsUrl],
  [
    "Business Registration",
    "Business registration information confirmed by applicant.",
  ],
  [
    "Evidence Checklist",
    "Desktop opt-in, mobile opt-in, consent checkbox, website footer, Privacy Policy, Terms",
  ],
];
export function SubmissionPackPage() {
  const toast = useToast();
  const { credentials } = useAssessmentSession();
  const [documents, setDocuments] = useState<
    Array<{ id: string; documentType: string; fileName: string }>
  >([]);
  useEffect(() => {
    if (!credentials || import.meta.env.VITE_USE_MOCK_ASSESSMENT === "true")
      return;
    void getAssessmentSnapshot(credentials).then((snapshot) =>
      setDocuments(snapshot.documents ?? []),
    );
  }, [credentials]);
  const copy = (value: string) =>
    void navigator.clipboard
      .writeText(value)
      .then(() => toast.show("Copied to clipboard"));
  const docs = [
    { title: "A2P Submission Pack", type: "PDF", icon: FilePdf },
    { title: "GHL Copy-Paste Answers", type: "DOCX", icon: FileDoc },
    { title: "Evidence Checklist", type: "PDF", icon: FilePdf },
  ];
  return (
    <main className="mx-auto max-w-[1180px] px-5 py-9 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-brand/10 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-brand">
            <Check size={15} weight="bold" />
            Assessment passed
          </span>
          <h1 className="mt-5 text-3xl font-semibold tracking-tight sm:text-4xl">
            Your submission pack is ready.
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-[#A0A5A8]">
            Review finalized answers, copy individual sections, or download
            private generated files when the assessment Worker is connected.
          </p>
        </div>
        <Button
          onClick={() =>
            copy(
              answers
                .map(([label, value]) => `${label}\n${value}`)
                .join("\n\n"),
            )
          }
        >
          <Clipboard size={17} />
          Copy all answers
        </Button>
      </div>
      {import.meta.env.VITE_USE_MOCK_ASSESSMENT === "true" && (
        <div className="mt-6 rounded-2xl border border-brand/20 bg-brand/[.06] px-4 py-3 text-sm text-[#D8F59B]">
          Development Assessment Simulation · Document buttons use mock records.
        </div>
      )}
      <div className="mt-7 grid gap-4 md:grid-cols-3">
        {docs.map(({ title, type, icon: Icon }) => (
          <Card key={title} className="min-h-52 p-5">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-brand/10 text-brand">
              <Icon size={22} />
            </span>
            <h2 className="mt-7 font-semibold">{title}</h2>
            <p className="mt-2 text-xs uppercase tracking-wider text-[#70777B]">
              Private generated file · {type}
            </p>
            <Button
              variant="secondary"
              className="mt-5 w-full"
              onClick={() => void downloadDocument(title)}
            >
              <DownloadSimple size={16} />
              Download
            </Button>
          </Card>
        ))}
      </div>
      <div className="mt-10 flex items-center justify-between">
        <div>
          <p className="eyebrow">GHL copy-paste answers</p>
          <h2 className="mt-2 text-2xl font-semibold">
            Finalized application content
          </h2>
        </div>
      </div>
      <div className="mt-5 space-y-3">
        {answers.map(([label, value]) => (
          <Card key={label} className="p-5">
            <div className="flex items-center justify-between gap-4">
              <p className="text-xs font-bold uppercase tracking-wider text-[#70777B]">
                {label}
              </p>
              <Button
                variant="ghost"
                className="px-3"
                onClick={() => copy(value)}
              >
                <Clipboard size={15} />
                Copy
              </Button>
            </div>
            <p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-[#F5F7F7]">
              {value}
            </p>
          </Card>
        ))}
      </div>
      <p className="mt-7 text-center text-xs text-[#70777B]">
        This submission pack never includes an EIN or other sensitive identity
        information.
      </p>
    </main>
  );
  async function downloadDocument(title: string) {
    if (!credentials || import.meta.env.VITE_USE_MOCK_ASSESSMENT === "true") {
      toast.show("Connect the assessment Worker to generate this private document");
      return;
    }
    const expected =
      title === "A2P Submission Pack"
        ? "submission_pack_pdf"
        : title === "GHL Copy-Paste Answers"
          ? "ghl_copy_paste_docx"
          : "evidence_checklist_pdf";
    const document = documents.find((item) => item.documentType === expected);
    if (!document) {
      toast.show("This document has not been generated yet");
      return;
    }
    const { signedUrl } = await getDownloadUrl(credentials, document.id);
    window.location.assign(signedUrl);
  }
}
